/**
 * SequencerResults Component
 *
 * Results screen displayed after completing a step sequencer exercise.
 * Shows score, time, per-step review with correct/incorrect highlighting,
 * and action buttons.
 *
 * Follows the same visual pattern as QuizResults but adapted for
 * step ordering results (position-based instead of question-based).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Clock,
  XCircle,
  Trophy,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  ShieldAlert,
  ArrowLeft,
  RotateCcw,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { formatTimer } from './PlayerShell';
import type { SequencerResultItem } from '@guild/shared';

interface SequencerResultsProps {
  /** Number of steps in correct position */
  correctPositions: number;
  /** Total number of steps */
  totalSteps: number;
  /** Score percentage (0-100) */
  score: number;
  /** Elapsed time in seconds */
  elapsedSeconds: number;
  /** Best score from practice stats (null if first attempt) */
  bestScore: number | null;
  /** Per-step results with correct/submitted positions */
  results: SequencerResultItem[];
  /** Original step data (keyed by stepId) for descriptions and safety flags */
  stepData: Map<string, { description: string; isSafetyCritical: boolean }>;
  /** Called when "Try Again" is tapped */
  onTryAgain: () => void;
  /** Task + section title for context */
  title: string;
}

export function SequencerResults({
  correctPositions,
  totalSteps,
  score,
  elapsedSeconds,
  bestScore,
  results,
  stepData,
  onTryAgain,
  title,
}: SequencerResultsProps) {
  const navigate = useNavigate();
  const [showReview, setShowReview] = useState(false);

  const incorrectCount = totalSteps - correctPositions;
  const isNewBest = score > (bestScore ?? -1);
  const isPerfect = score === 100;

  // Sort results by correct position for the review list
  const sortedResults = [...results].sort((a, b) => a.correctPosition - b.correctPosition);

  return (
    <div className="min-h-screen bg-op-surface">
      <div className="mx-auto max-w-lg px-4 py-8">
        {/* Score display */}
        <div
          className="flex flex-col items-center text-center"
          style={{ animation: 'fadeInUp 0.4s ease-out' }}
        >
          {/* Icon */}
          <div className={cn(
            'flex h-20 w-20 items-center justify-center rounded-full',
            isPerfect ? 'bg-guild-mint/50' : score >= 60 ? 'bg-emerald-100' : 'bg-amber-100',
          )}>
            <Target
              size={36}
              strokeWidth={1.5}
              className={cn(
                isPerfect ? 'text-guild-green' : score >= 60 ? 'text-emerald-600' : 'text-amber-600',
              )}
            />
          </div>

          {/* Score fraction */}
          <p className="mt-5 text-4xl font-bold text-op-text-primary">
            {correctPositions} / {totalSteps}
          </p>

          {/* Score percentage */}
          <p className={cn(
            'mt-1 text-lg font-semibold',
            isPerfect ? 'text-guild-green' : score >= 60 ? 'text-emerald-600' : 'text-amber-600',
          )}>
            {score}% Correct Order
          </p>

          {/* New best badge */}
          {isNewBest && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-guild-mint/50 px-3 py-1"
              style={{ animation: 'fadeInUp 0.4s ease-out 0.2s both' }}
            >
              <Trophy size={14} strokeWidth={1.5} className="text-guild-green" />
              <span className="text-xs font-semibold text-guild-green">New Best Score!</span>
            </div>
          )}

          {/* Perfect score message */}
          {isPerfect && (
            <p className="mt-3 text-sm font-medium text-guild-green">
              Perfect order! You know this procedure inside and out.
            </p>
          )}

          {/* Title context */}
          <p className="mt-4 text-sm text-op-text-secondary line-clamp-2">{title}</p>
        </div>

        {/* Stat pills */}
        <div
          className="mt-6 grid grid-cols-3 gap-3"
          style={{ animation: 'fadeInUp 0.4s ease-out 0.1s both' }}
        >
          <StatPill
            icon={Clock}
            value={formatTimer(elapsedSeconds)}
            label="Time"
          />
          <StatPill
            icon={XCircle}
            value={String(incorrectCount)}
            label="Misplaced"
            variant={incorrectCount > 0 ? 'danger' : 'success'}
          />
          <StatPill
            icon={Trophy}
            value={bestScore !== null ? `${Math.max(bestScore, score)}%` : `${score}%`}
            label="Best"
            variant="success"
          />
        </div>

        {/* Action buttons */}
        <div
          className="mt-8 space-y-3"
          style={{ animation: 'fadeInUp 0.4s ease-out 0.2s both' }}
        >
          {/* Review Steps toggle */}
          <button
            type="button"
            onClick={() => setShowReview(!showReview)}
            className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-op-text-primary ring-1 ring-gray-200 transition-all duration-150 active:bg-gray-50 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            {showReview ? (
              <>
                <ChevronUp size={18} strokeWidth={1.5} />
                Hide Step Review
              </>
            ) : (
              <>
                <ChevronDown size={18} strokeWidth={1.5} />
                Review Correct Order
              </>
            )}
          </button>

          {/* Try Again */}
          <button
            type="button"
            onClick={onTryAgain}
            className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-guild-green px-6 py-3 text-sm font-semibold text-white active:scale-[0.98] active:bg-guild-green/90 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            <RotateCcw size={18} strokeWidth={1.5} />
            Try Again
          </button>

          {/* Back to Content */}
          <button
            type="button"
            onClick={() => navigate('/operator/content')}
            className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-op-muted px-6 py-3 text-sm font-semibold text-op-text-primary active:bg-gray-200 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
            Back to Content
          </button>
        </div>

        {/* Step review list (sorted by correct position) */}
        {showReview && (
          <div
            className="mt-6 space-y-3"
            style={{ animation: 'fadeInUp 0.3s ease-out' }}
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-op-text-tertiary">
              Correct Step Order
            </p>
            {sortedResults.map((result) => {
              const step = stepData.get(result.stepId);
              return (
                <div
                  key={result.stepId}
                  className={cn(
                    'rounded-2xl bg-white p-4 ring-1',
                    result.isCorrect ? 'ring-emerald-200' : 'ring-red-200',
                    result.isCorrect ? 'border-l-4 border-emerald-500' : 'border-l-4 border-red-500',
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Correct/incorrect icon */}
                    {result.isCorrect ? (
                      <Check
                        size={18}
                        strokeWidth={2}
                        className="mt-0.5 flex-shrink-0 text-emerald-600"
                      />
                    ) : (
                      <X
                        size={18}
                        strokeWidth={2}
                        className="mt-0.5 flex-shrink-0 text-red-600"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      {/* Position badge */}
                      <div className="mb-1 flex items-center gap-2">
                        <span className={cn(
                          'inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold',
                          result.isCorrect ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700',
                        )}>
                          {result.correctPosition + 1}
                        </span>
                        {step?.isSafetyCritical && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5">
                            <ShieldAlert size={10} strokeWidth={1.5} className="text-red-600" />
                            <span className="text-[9px] font-semibold text-red-600">Safety</span>
                          </span>
                        )}
                      </div>
                      {/* Step description */}
                      <p className="text-sm font-medium text-op-text-primary">
                        {step?.description ?? `Step ${result.correctPosition + 1}`}
                      </p>
                      {/* Misplaced hint */}
                      {!result.isCorrect && (
                        <p className="mt-1 text-xs text-red-600">
                          You placed this at position #{result.placedPosition + 1}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// StatPill (internal — same pattern as QuizResults)
// ============================================

function StatPill({
  icon: Icon,
  value,
  label,
  variant = 'default',
}: {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  value: string;
  label: string;
  variant?: 'default' | 'success' | 'danger';
}) {
  return (
    <div className="flex flex-col items-center rounded-2xl bg-white p-3 ring-1 ring-black/[0.04]">
      <Icon
        size={16}
        strokeWidth={1.5}
        className={cn(
          variant === 'success' && 'text-emerald-600',
          variant === 'danger' && 'text-red-500',
          variant === 'default' && 'text-op-text-tertiary',
        )}
      />
      <p className="mt-1.5 text-lg font-bold text-op-text-primary">{value}</p>
      <p className="text-xs text-op-text-tertiary">{label}</p>
    </div>
  );
}
